import { inject, Injectable, Injector, runInInjectionContext, Type } from '@angular/core';
import { ApizUIProcess } from '@compiere-ws/models/apiz-domains';
import { CompierePrecisionData } from '@compiere-ws/models/compiere-precision-json';
import { CompiereProcessService } from '@compiere-ws/services/compiere-process/compiere-process.service';
import { PrecisionDataService } from '@compiere-ws/services/precision/precision.service';
import SpecificWindowUiComponent from '@iupics-components/specific/window/specific-window-ui/specific-window-ui.component';
import { IupicsField } from '@iupics-manager/models/iupics-data';
import { TranslateService } from '@ngx-translate/core';
import { finalize, Observable, of, share, Subject, zip } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { UICreatorService } from '../ui-creator/ui-creator.service';

@Injectable({
  providedIn: 'root',
})
export class CacheManagerService {
  static process_Params_caching = new Map<number, ApizUIProcess>();

  static productAttributes_caching = new Map<string, IupicsField[]>();

  static iupics_components = new Map<string, Type<any>>();
  static iupics_specific_window = new Map<string, Type<SpecificWindowUiComponent>>();
  static iupics_widgets = new Map<string, Type<any>>();

  static chatTags = new Map<number, any>();

  private _obsCache: Map<string, Observable<any>> = new Map<string, Observable<any>>();
  private readonly _cachingDataReset$ = new Subject<void>();
  /** Emits after resetCachingData so other front caches can drop their data */
  readonly cachingDataReset$ = this._cachingDataReset$.asObservable();
  private _cache: Map<string, Map<any, any>> = new Map<string, Map<number, any>>();
  /** increased by each reset: a request started before (previous role) must not fill the new caches */
  #generation = 0;

  #precisionDataService = inject(PrecisionDataService);
  #processService = inject(CompiereProcessService);
  #translateService = inject(TranslateService);

  // Default Cache
  getCache(cacheName: CacheName | string): Map<number, any> {
    if (!this._cache.has(cacheName)) {
      this._cache.set(cacheName, new Map<number, any>());
    }
    return this._cache.get(cacheName)!;
  }

  setCache(cacheName: CacheName | string, value: any) {
    this._cache.set(cacheName, value);
  }

  getCacheValueById(cacheName: CacheName | string, id: any): any {
    return this.getCache(cacheName).get(id);
  }
  setCacheValueById(cacheName: CacheName | string, id: any, value: any) {
    this.getCache(cacheName).set(id, value);
  }

  /**
   *
   * @param obs Observable to share
   * @param obsIdentifier Unique identifier of observable
   * @param options {
   * id : Unique identifier within the cache defined,
   * cacheName : cache used,
   * getObsResultProcessing: Function which override getObsResultProcessing behaviour  }
   * @returns
   */
  getSharableObs(
    obs: Observable<any>,
    obsIdentifier: string,
    options: SharableObsOptions = {
      cached: false,
    }
  ): Observable<any> {
    options = {
      ...{
        cached: false,
        cacheName: CacheName.DEFAULT,
      },
      ...options,
    };
    let observable: Observable<any>;
    const cached = options.cached;
    const cacheName = options.cacheName;
    const id = options.id;
    const getObsResultProcessing = options.getObsResultProcessing;

    if (cached && id === undefined) {
      throw new Error('Observable result could not be cached: id not defined.');
    }
    if (cached && this.getCacheValueById(cacheName, id) !== undefined) {
      observable = of([this.getCacheValueById(cacheName, id)]);
    } else if (this._obsCache.has(obsIdentifier)) {
      observable = this._obsCache.get(obsIdentifier);
    } else {
      const generation = this.#generation;
      const obsCache = this._obsCache;
      observable = obs.pipe(
        switchMap((res) => {
          if (generation !== this.#generation) {
            // answer of the previous session: given to its caller, not cached
            return of(res);
          }
          if (getObsResultProcessing) {
            return getObsResultProcessing(res, options);
          } else {
            return this.getObsResultProcessing(res, options);
          }
        }),
        share(),
        finalize(() => {
          // the map of its session (a reset replaced it)
          if (obsCache.get(obsIdentifier) === observable) obsCache.delete(obsIdentifier);
        })
      );
      this._obsCache.set(obsIdentifier, observable);
    }
    return observable;
  }
  private getObsResultProcessing(res: any, options: SharableObsOptions) {
    if (options.cached) {
      this.setCacheValueById(options.cacheName, options.id, res);
    }
    return of(res);
  }

  getCurrencySymbol(precisionDataRequest: CompierePrecisionData) {
    return this.getPrecisionDatas([precisionDataRequest]).pipe(
      switchMap((precisions: CompierePrecisionData[]) => {
        let symbol = '';
        if (precisions && precisions[0]) {
          symbol = precisions[0].curSymbol;
        }
        return of(symbol);
      })
    );
  }

  getPrecision(precisionDataRequest: CompierePrecisionData) {
    return this.getPrecisionDatas([precisionDataRequest]).pipe(
      switchMap((precisions: CompierePrecisionData[]) => {
        let precision = 2;
        if (precisions && precisions[0]) {
          if (['M_PriceList', 'M_PriceList_Version'].includes(precisions[0].tableName)) {
            precision = precisions[0].pricePrecision;
          } else {
            precision = precisions[0].stdPrecision;
          }
        }
        return of(precision);
      })
    );
  }

  getPrecisionDatas(precisionDataRequests: CompierePrecisionData[]): Observable<{}> {
    const cacheId = this.generatePrecisionDataId(precisionDataRequests[0].tableName, precisionDataRequests[0].id);
    const listToGet = precisionDataRequests.filter(
      (pr) => !this.getCacheValueById(CacheName.PRECISION_CURRENCY, this.generatePrecisionDataId(pr.tableName, pr.id))
    );
    // a running request is shared only for the same list (keyed on its first item, two different lists shared
    // one request and the second one's precisions were never read)
    const obsIdentifier =
      CacheName.PRECISION_CURRENCY +
      '_' +
      (precisionDataRequests.length === 1
        ? cacheId
        : precisionDataRequests
            .map((pr) => this.generatePrecisionDataId(pr.tableName, pr.id))
            .sort()
            .join('|'));
    return this.getSharableObs(
      listToGet.length > 0 ? this.#precisionDataService.getPrecisionDatas(listToGet) : of([]),
      obsIdentifier,
      {
        cached: precisionDataRequests.length === 1,
        cacheName: CacheName.PRECISION_CURRENCY,
        id: cacheId,
        getObsResultProcessing: (precisions: CompierePrecisionData[], options: SharableObsOptions) => {
          if (precisions) {
            for (const precision of precisions) {
              this.setCacheValueById(
                CacheName.PRECISION_CURRENCY,
                this.generatePrecisionDataId(precision.tableName, precision.id),
                precision
              );
            }
          }
          const list = [];
          for (const pr of precisionDataRequests) {
            list.push(
              this.getCacheValueById(CacheName.PRECISION_CURRENCY, this.generatePrecisionDataId(pr.tableName, pr.id))
            );
          }
          return of(list);
        },
      }
    );
  }
  generatePrecisionDataId(tableName: string, id: number) {
    return tableName + '_' + id;
  }

  loadProcessInCache(processId: number, injector: Injector, source?: Observable<ApizUIProcess>) {
    this.getProcess(processId, injector, source).subscribe();
  }

  getProcess(processId: number, injector: Injector, source?: Observable<ApizUIProcess>) {
    if (!source) {
      source = this.#processService.getProcess(processId);
    }
    const process = CacheManagerService.process_Params_caching.get(processId);
    let observable: Observable<any>;
    const obsIdentifier = 'Process_' + processId;
    if (process) {
      observable = of(process);
    } else if (this._obsCache.has(obsIdentifier)) {
      observable = this._obsCache.get(obsIdentifier);
    } else {
      const generation = this.#generation;
      const obsCache = this._obsCache;
      observable = source.pipe(
        switchMap((res) => {
          this.#parseProcessResult(res, injector);
          // a process read for the previous role is not kept for the new one
          if (generation === this.#generation) {
            CacheManagerService.process_Params_caching.set(processId, res);
          }
          return of(res);
        }),
        share(),
        finalize(() => {
          if (obsCache.get(obsIdentifier) === observable) obsCache.delete(obsIdentifier);
        })
      );
      this._obsCache.set(obsIdentifier, observable);
    }
    return observable;
  }

  #parseProcessResult(process: ApizUIProcess, injector: Injector) {
    //load in cache
    const arr = [];
    runInInjectionContext(injector, () => {
      const uiCreator = inject(UICreatorService);
      for (const field of process.fields ?? []) {
        if (field.isRange) {
          field.data.label += ' ' + this.#translateService.instant('rangedField.from');
        } else if (field?.data?.fromRange) {
          field.data.label = this.#translateService.instant('rangedField.to');
        }
        if (field.processId && field.processId != process.process.AD_Process_ID) {
          this.loadProcessInCache(field.processId, injector);
        } else if (field.formId) {
          arr.push(uiCreator.getSpecificWindow(field.formId));
        }
      }
    });

    if (arr.length > 0) {
      zip(...arr).subscribe();
    }
    // CacheManagerService.process_Params_caching.set(processId, process);

    return arr;
  }

  flatten(children: any[]): any[] {
    return children
      .map((child) => {
        if (child.children.length > 0) {
          return [...this.flatten(child.children)];
        } else {
          return [child];
        }
      })
      .flat();
  }

  // Tools
  resetCachingData() {
    CacheManagerService.productAttributes_caching = new Map();
    CacheManagerService.process_Params_caching = new Map();
    CacheManagerService.chatTags = new Map();
    this._cache = new Map<string, Map<any, any>>();
    // requests in flight belong to the previous session: new requests start fresh
    this._obsCache = new Map<string, Observable<any>>();
    this.#generation++;
    this._cachingDataReset$.next();
  }
}
export enum CacheName {
  DEFAULT = 'DEFAULT',
  PRECISION_CURRENCY = 'PRECISION_CURRENCY',
}
export enum TableName {
  CURRENCY = 'C_Currency',
  PRICE_LIST_VERSION = 'M_PriceList_Version',
  PRICE_LIST = 'M_PriceList',
  ACCT_SCHEMA = 'C_AcctSchema',
  UOM = 'C_UOM',
}
export type SharableObsOptions = {
  cached: boolean;
  id?: any;
  cacheName?: CacheName | string;
  getObsResultProcessing?: Function;
};
